// Operating Health Score — the "how is the ongoing business doing?"
// counterpart to Launch readiness. Only meaningful for founders whose
// business is already operating; pre-launch founders see it locked.
//
// Aug 2026 (second update): the leg mix + weights refactored again per
// user request. Runway and Compliance were removed as top-level legs
// (both are already nested inside Funding Health and Launch Health
// respectively). The composite now blends 4 higher-level rollups:
//
//   • Revenue trend     — 30% — last 3-mo revenue vs prior 3-mo revenue.
//                          flat = 50, doubled = 100, halved+ = 0.
//   • Funding Health    — 30% — composite of Funding Health strip legs.
//   • Marketing         — 20% — composite of Marketing Health strip legs.
//   • Launch Health     — 20% — `launchReadinessPct(business)` (blend of
//                          plan progress, roadmap progress, compliance).
//
// Missing legs are treated as zero contributions rather than skipped.
// The denominator is always the full weight (30+30+20+20 = 100), so a
// founder with no revenue signal AND no marketing signal can't have
// those absences flatter their score — the missing evidence itself is
// the news.

import { computeFundingHealth } from "./fundingHealth";
import { computeMarketingHealth } from "./marketingHealth";
import { launchReadinessPct } from "./launchReadiness";

const WEIGHTS = { revenue: 30, funding_health: 30, marketing: 20, launch_health: 20 };

function _sumRow(row) {
  if (!row || typeof row !== "object") return 0;
  return Object.values(row).reduce((s, v) => s + (Number(v) || 0), 0);
}

// Last-12-month keys (YYYY-MM) ending this month, newest first.
function _recentMonthKeys(n = 12) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export function revenueTrendLeg(business) {
  const actuals = business?.budget_revenue_actuals || {};
  const months = _recentMonthKeys(6);
  const recent3 = months.slice(0, 3).reduce((s, k) => s + _sumRow(actuals[k]), 0);
  const prior3 = months.slice(3, 6).reduce((s, k) => s + _sumRow(actuals[k]), 0);
  if (recent3 === 0 && prior3 === 0) return null;
  if (prior3 === 0) return 100; // brand-new revenue is a big positive signal
  const ratio = recent3 / prior3;
  // ratio 1.0 → 50, 2.0+ → 100, 0.5- → 0.
  const clamped = Math.max(0, Math.min(2, ratio));
  return Math.round((clamped / 2) * 100);
}

export function fundingHealthLeg(business) {
  // Aug 2026 · replaces `activityLeg`. Delegates to `computeFundingHealth`
  // which returns { status, pct, legs }. Only meaningful once a plan
  // exists — pre-plan surfaces return null so the leg is treated as
  // "no signal" (contributes 0 under the missing-legs-count-as-zero
  // policy). Also gates on the "unknown" status so a brand-new plan
  // with zero cap-stack / runway signal doesn't score 0 misleadingly.
  const fh = computeFundingHealth(business);
  if (!fh || fh.status === "unknown" || fh.legs == null) return null;
  return fh.pct;
}

export function marketingLeg(business) {
  // Aug 2026 · Marketing Health composite promoted to a top-level leg
  // on Operating Health. Pre-plan surfaces return null so the leg is
  // treated as "no signal" (contributes 0 under the missing-legs-
  // count-as-zero policy).
  const mh = computeMarketingHealth(business);
  if (!mh || mh.status === "unknown" || mh.legs == null) return null;
  return mh.pct;
}

export function launchHealthLeg(business) {
  // Aug 2026 · Launch Health composite (plan+roadmap+compliance blend)
  // promoted to a top-level leg on Operating Health. Returns null when
  // there is no plan yet so the leg is treated as "no signal".
  if (!business?.plan) return null;
  const pct = launchReadinessPct(business);
  if (pct == null || isNaN(pct)) return null;
  return pct;
}

export function operatingHealthBreakdown(business) {
  return {
    revenue: revenueTrendLeg(business),
    funding_health: fundingHealthLeg(business),
    marketing: marketingLeg(business),
    launch_health: launchHealthLeg(business),
  };
}

export function operatingHealthPct(business) {
  // Aug 2026 · Do NOT ignore missing legs. A leg that returns `null`
  // contributes 0 to the numerator; the denominator stays pinned at
  // the full weight sum so a plan with zero signals scores 0, and a
  // plan that only has one leg reporting can't inflate the total by
  // re-normalising the other 80% away.
  const b = operatingHealthBreakdown(business);
  const totalWeight = Object.values(WEIGHTS).reduce((s, w) => s + w, 0);
  let num = 0;
  Object.entries(b).forEach(([k, v]) => {
    if (v == null) return; // contributes 0
    num += v * WEIGHTS[k];
  });
  if (totalWeight === 0) return 0;
  return Math.round(num / totalWeight);
}

export function operatingHealthStage(score) {
  if (score >= 80) return { label: "Thriving", color: "#059669", step: 5 };
  if (score >= 60) return { label: "Steady", color: "#7C5FB0", step: 4 };
  if (score >= 40) return { label: "Mixed", color: "#F59E0B", step: 3 };
  if (score >= 20) return { label: "Under pressure", color: "#B05F6D", step: 2 };
  return { label: "Getting started", color: "#6B7280", step: 1 };
}

export { WEIGHTS as OPERATING_HEALTH_WEIGHTS };
// Re-export the pure-math delta helper so callers can `import { computeWeeklyDelta }`
// from a single place without knowing the extraction detail.
export { computeWeeklyDelta } from "./operatingHealthDelta";
